import {
  AddressType,
  BaseDocumentType,
  ComplianceObjectType,
  RiskRating,
} from "~/graphql/supergraphEnums";
import { FieldKey } from "~/pages/Compliance/ProposedChangesModal/constants";
import { type SelectionSource } from "./constants";
import type { ComparisonRow, ProfileData } from "./types";
import type {
  Address,
  ApplyProposedChangesSelectionInput,
  ComplianceIndividualProfileInput,
  ComplianceInstitutionProfileInput,
  ComplianceJointProfileInput,
  JointAddress,
  ReconcileDocumentSelection,
  RiskRatingInput,
} from "~/graphql/supergraphTypes";

export type BuildPayloadArgs = {
  caseId: number;
  targetProfileId: number;
  programId: number | undefined;
  latestVersionId: number | null;
  rows: ComparisonRow[];
  getSelection: (rowKey: string) => SelectionSource;
  currentProfile: ProfileData | undefined;
  publishedProfile: ProfileData | undefined;
  proposedProfile: ProfileData | undefined;
};

const pickProfile = (
  source: SelectionSource,
  currentProfile: ProfileData | undefined,
  publishedProfile: ProfileData | undefined,
  proposedProfile: ProfileData | undefined,
): ProfileData | undefined => {
  if (source === "current") return currentProfile;
  if (source === "recentlyPublished") return publishedProfile;
  return proposedProfile;
};

const profileIdOf = (profile: ProfileData | undefined): number | undefined => profile?.id;

const buildAddresses = (profile: ProfileData): Address[] =>
  (profile.addresses ?? []).map((address) => ({
    addressType: AddressType.Unknown,
    street: address.street,
    street2: address.street2,
    city: address.city,
    state: address.state?.code || address.province,
    postalCode: address.postalCode,
    country: address.country?.alpha2code,
  }));

const buildJointAddresses = (profile: ProfileData): JointAddress[] => {
  const primary =
    profile.addresses?.find((address) => address.isPrimaryDomicile) ?? profile.addresses?.[0];
  return primary?.country?.alpha2code
    ? [{ addressType: "primary_domicile", domicile: primary.country.alpha2code }]
    : [];
};

export const buildMutationPayload = ({
  caseId,
  targetProfileId,
  programId,
  latestVersionId,
  rows,
  getSelection,
  currentProfile,
  publishedProfile,
  proposedProfile,
}: BuildPayloadArgs): ApplyProposedChangesSelectionInput => {
  const reviewedProfileId = latestVersionId ?? undefined;

  if (!currentProfile || !proposedProfile) {
    return {
      caseId,
      proposedProfileId: targetProfileId,
      reviewedProfileId,
    };
  }

  const rowsByGroupKey = new Map(rows.map((row) => [row.selectionGroupKey, row]));

  const selectedProfileForGroup = (groupKey: string): ProfileData | undefined => {
    if (!rowsByGroupKey.has(groupKey)) return undefined;
    return pickProfile(
      getSelection(groupKey),
      currentProfile,
      publishedProfile,
      proposedProfile,
    );
  };

  const profileForField = (groupKey: string): ProfileData =>
    selectedProfileForGroup(groupKey) ?? currentProfile;

  const reconcileIdForGroup = (groupKey: string): number | undefined => {
    const profile = selectedProfileForGroup(groupKey);
    if (!profile) return undefined;
    const id = profileIdOf(profile);
    if (id === undefined || id === targetProfileId) return undefined;
    return id;
  };

  const buildIndividual = (): ComplianceIndividualProfileInput => {
    const name = profileForField(FieldKey.LegalName);
    const accountTypeProfile = profileForField(FieldKey.AccountType);
    return {
      caseId,
      accountType:
        accountTypeProfile.accountType as ComplianceIndividualProfileInput["accountType"],
      firstName: name.firstName ?? "",
      middleName: name.middleName ?? undefined,
      lastName: name.lastName ?? "",
      dateOfBirth: profileForField(FieldKey.DateOfBirth).dateOfBirth ?? undefined,
      taxId: profileForField(FieldKey.TaxId).taxIdUnencrypted ?? "",
      taxIdType: profileForField(FieldKey.TaxIdType).taxIdType ?? undefined,
      addresses: buildAddresses(profileForField(FieldKey.Address)),
    };
  };

  const buildInstitution = (): ComplianceInstitutionProfileInput => {
    const accountTypeProfile = profileForField(FieldKey.AccountType);
    return {
      caseId,
      accountType:
        accountTypeProfile.accountType as ComplianceInstitutionProfileInput["accountType"],
      legalBusinessName: profileForField(FieldKey.LegalName).legalName ?? "",
      dateOfFormation:
        profileForField(FieldKey.DateOfOrganization).dateOfOrganization ?? undefined,
      taxId: profileForField(FieldKey.TaxId).taxIdUnencrypted ?? "",
      taxIdType: profileForField(FieldKey.TaxIdType).taxIdType ?? undefined,
      amlEntityId: profileForField(FieldKey.AmlQuestion).amlEntityTypeId ?? -1,
      addresses: buildAddresses(profileForField(FieldKey.Address)),
    };
  };

  const buildJoint = (): ComplianceJointProfileInput => {
    const accountTypeProfile = profileForField(FieldKey.AccountType);
    return {
      caseId,
      accountType: accountTypeProfile.accountType as ComplianceJointProfileInput["accountType"],
      legalBusinessName: profileForField(FieldKey.LegalName).legalName ?? "",
      amlEntityId: profileForField(FieldKey.AmlQuestion).amlEntityTypeId ?? -1,
      addresses: buildJointAddresses(profileForField(FieldKey.Address)),
    };
  };

  const reconcileDocuments: ReconcileDocumentSelection[] = rows
    .filter((row) => row.fieldKey === FieldKey.Document)
    .flatMap((row) => {
      const fromProfileId = reconcileIdForGroup(row.selectionGroupKey);
      if (fromProfileId === undefined) return [];
      return [
        {
          baseType: (row.baseType as BaseDocumentType | undefined) ?? BaseDocumentType.Other,
          fromProfileId,
        },
      ];
    });

  const buildRiskRating = (): RiskRatingInput | undefined => {
    if (programId == null) return undefined;
    const profile = selectedProfileForGroup(FieldKey.RiskRating);
    if (!profile) return undefined;
    const overriddenRating = profile.riskRating?.overriddenRating ?? RiskRating.None;
    return { overriddenRating, profileId: targetProfileId, programId };
  };

  const objectType = currentProfile.complianceObjectType;

  return {
    caseId,
    proposedProfileId: targetProfileId,
    reviewedProfileId,
    individualProfile:
      objectType === ComplianceObjectType.Individual ? buildIndividual() : undefined,
    jointProfile: objectType === ComplianceObjectType.Joint ? buildJoint() : undefined,
    institutionProfile:
      objectType === ComplianceObjectType.Institution ? buildInstitution() : undefined,
    reconcileOwnersFromProfileId: reconcileIdForGroup(FieldKey.Owners),
    reconcileControllersFromProfileId: reconcileIdForGroup(FieldKey.Controllers),
    reconcileDocuments: reconcileDocuments.length > 0 ? reconcileDocuments : undefined,
    riskRating: buildRiskRating(),
  };
};
